import { UiSchema } from '@rjsf/core'
import classNames from 'classnames'
import { JSONSchema7 } from 'json-schema'
import { useCallback, useEffect, useState } from 'react'
import GetLogo from '../../components/componentLogos'
import KindColor from '../../components/kindColors'
import { NodeAction } from '../editor/graph/NodeData'
import ErrorDisplay from '../../components/error/ErrorDisplay'
import Form from '../form/ComponentForm'

import './CreateComponent.css'
import styles from './CreateComponent.module.css'
import { getHeaders } from '../../utils/common'
import { axiosPrivate } from '../../utils/axiosInstance'
import { DialogWrapper } from '../../components/general/DialogWrapper'
import Loading from '../../Loading'

let cachedOptions: {
  [key: string]: any[] | undefined
} = {}

function getLogo(type: string) {
  const logoName = GetLogo({ type })
  if (logoName === null) {
    return null
  }
  return <img className={styles.logoImg} height="20px" alt="logo" src={`/img/logos/${logoName}`} />
}

function capitalize(str: string) {
  return str.replace(/^\w/, (c) => c.toUpperCase())
}

type KindOption = {
  name: string
  categories: string[]
  is_deprecated: boolean
  is_ota: boolean
}

type ConfigInfo = {
  schema: JSONSchema7
  uiSchema: UiSchema
}

interface CreateComponentParams {
  sessionID: string
  fileName: string
  setTree: any
  isOpen: boolean
  closeModal: () => void
  actions: NodeAction[]
}

/**
 * Component for creating a new component within the Flow Editor.
 *
 * @param {Object} params - The parameters for rendering the component creation dialog.
 * @param {string} params.sessionID - The session ID.
 * @param {string} params.fileName - The name of the file.
 * @param {function} params.setTree - A callback function to set the tree data after creating the component.
 * @param {boolean} params.isOpen - Indicates whether the component creation dialog is open.
 * @param {function} params.closeModal - A callback function to close the component creation dialog.
 * @param {Array} params.actions - An array of available actions for creating components.
 * @returns {JSX.Element} The JSX element representing the `CreateComponent` component.
 */
export default function CreateComponent({ sessionID, fileName, setTree, isOpen, closeModal, actions }: CreateComponentParams) {
  const [chosenAction, setChosenAction] = useState<NodeAction | null>(null)

  const [isLoading, setLoading] = useState(false)
  const [fetchError, setFetchError] = useState(null)
  const [kindOptions, setKindOptions] = useState<KindOption[] | null>(null)

  const [kind, setKind] = useState('')
  const [type, setType] = useState('')
  const [typeSearch, setTypeSearch] = useState('')

  const [configInfo, setConfigInfo] = useState<ConfigInfo | null>(null)

  const myHeaders = getHeaders()
  myHeaders.append('Content-Type', 'application/json')

  useEffect(() => {
    if (actions.length === 1) {
      setChosenAction(actions[0])
    } else {
      setChosenAction(null)
    }
  }, [actions, setChosenAction])

  useEffect(() => {
    setLoading(false)
    setFetchError(null)
    setKindOptions(null)
    setKind('')
    setType('')
    setTypeSearch('')
    setConfigInfo(null)
  }, [chosenAction, isOpen, closeModal])

  useEffect(() => {
    cachedOptions = {}
  }, [sessionID, fileName])

  useEffect(() => {
    setConfigInfo(null)
  }, [type])

  // Get kind options if they're not set
  useEffect(() => {
    if (kindOptions !== null || chosenAction === null || chosenAction.kind === undefined) {
      return
    }

    setKind(chosenAction.kind)
    if (cachedOptions[chosenAction.kind] !== undefined) {
      setKindOptions(cachedOptions[chosenAction.kind] || null)
      return
    }

    let subscribed = true
    setLoading(true)
    fetch(`/mqttapi/v1/session/${sessionID}/list/${chosenAction.kind}`, {
      headers: myHeaders,
      method: 'GET',
    })
      .then(async (response) => {
        if (response.ok) {
          return response.json()
        }
        const text = await response.text()
        throw new Error(`${response.statusText}: ${text}`)
      })
      .then((json) => {
        if (subscribed) {
          if (chosenAction.kind) {
            cachedOptions[chosenAction.kind] = json
          }
          setKindOptions(json)
        }
      })
      .catch((error) => (subscribed ? setFetchError(error) : null))
      .finally(() => (subscribed ? setLoading(false) : null))

    return () => {
      subscribed = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kindOptions, sessionID, chosenAction, setKindOptions, setFetchError, setLoading])

  useEffect(() => {
    if (kindOptions === null || type === '' || chosenAction === null || configInfo !== null) {
      return
    }

    let subscribed = true
    setLoading(true)
    fetch(`/mqttapi/v1/session/${sessionID}/config/${chosenAction.kind}/${type}`, {
      headers: myHeaders,
      method: 'GET',
    })
      .then(async (response) => {
        if (response.ok) {
          return response.json()
        }
        const text = await response.text()
        throw new Error(`${response.statusText}: ${text}`)
      })
      .then((json) => {
        if (!subscribed) {
          return
        }

        const tmpInfo: ConfigInfo = {
          schema: {
            type: 'object',
            properties: {},
            additionalProperties: false,
          },
          uiSchema: {
            label: {
              classNames: styles.labelField,
            },
          },
        }

        // Only show label field if the type is not a resource
        if (type !== 'resource' && tmpInfo.schema.properties) {
          tmpInfo.schema.properties.label = {
            type: 'string',
          }
          // If we're placing this component in a resource namespace then label
          // is a required field
          if ((chosenAction.path || '').endsWith('_resources')) {
            tmpInfo.schema.required = ['label']
          }
        }
        if (json.schema && tmpInfo.schema.properties) {
          tmpInfo.schema.properties.component = json.schema
        }
        if (json.uiSchema) {
          tmpInfo.uiSchema.component = json.uiSchema
        }
        setConfigInfo(tmpInfo)
      })
      .catch((error) => (subscribed ? setFetchError(error) : null))
      .finally(() => (subscribed ? setLoading(false) : null))

    return () => {
      subscribed = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kindOptions, type, configInfo, sessionID, chosenAction, setConfigInfo, setFetchError, setLoading])

  const showActionOptions = useCallback(() => {
    return (
      <div className={styles.modalActionBtnGroup}>
        {(actions || []).map((action, idx) => (
          <button className={styles.modalActionBtn} key={idx} onClick={() => setChosenAction(action)}>
            <div
              className={styles.modalActionTitle}
              style={{
                borderBottom: `solid 2px ${KindColor({
                  kind: action.kind || '',
                })}`,
              }}
            >
              {capitalize(action.operation)} {action.kind}
            </div>
          </button>
        ))}
      </div>
    )
  }, [actions, setChosenAction])

  const showKindOptions = useCallback(() => {
    return (
      <>
        {isOpen ? (
          <>
            <div className={styles.configHeader}>
              {kind !== '' && <h4>Choose {kind}</h4>}
              <input
                className={classNames('form-control', styles.search)}
                type="text"
                value={typeSearch}
                onChange={(event) => setTypeSearch(event.currentTarget.value)}
                placeholder="🔍 Search..."
              />
            </div>
            <div className={styles.modalActionBtnGroup}>
              {(kindOptions || [])
                .filter((opt) => {
                  if (opt.is_deprecated) {
                    return false
                  }
                  if (typeSearch === '') {
                    return true
                  }
                  const searchTerms = typeSearch.split(' ')
                  const content = `${opt.name.toLowerCase()} ${(opt.categories || []).join(' ').toLowerCase()}`
                  return searchTerms.every((term) => {
                    return content.includes(term.toLowerCase())
                  })
                })
                .sort((a, b): number => {
                  if (a.name.includes(typeSearch)) {
                    return -1
                  }
                  if (b.name.includes(typeSearch)) {
                    return 1
                  }
                  return 0
                })
                .map((opt, idx) => (
                  <button className={opt.is_ota ? styles.modalActionBtnWithRectangle : styles.modalActionBtn } key={idx} onClick={() => setType(opt.name)}>
                    {getLogo(opt.name)}
                    <strong className={styles.modalActionName}>{opt.name}</strong>
                    <div className={styles.tagGroup}>
                      {(opt.categories || []).map((cat, idx) => (
                        <div key={idx}>{cat}</div>
                      ))}
                    </div>
                  </button>
                ))}
            </div>
          </>
        ) : null}
      </>
    )
  }, [kind, setType, kindOptions, typeSearch, isOpen])

  const submitConfigChanges = useCallback(
    (ctx:any) => {
      if (!chosenAction) {
        return
      }
      const value: any = {}
      value.type = type
      if (ctx.formData.component !== undefined) {
        value[type] = ctx.formData.component
      } else {
        switch (ctx.schema?.properties?.component?.type || '') {
          case 'string':
            value[type] = ''
            break
          case 'object':
            value[type] = {}
            break
          case 'array':
            value[type] = []
            break
          default:
            value[type] = null
        }
      }
      if ((ctx.formData.label || '') !== '') {
        value.label = ctx.formData.label
      }

      const patches = [
        {
          operation: chosenAction.operation,
          path: chosenAction.path,
          value: value,
        },
      ]

      // * fetching tree using axios as it is removing cors error
      // * previously it was from fetch api
      const config = {
        method: 'post',
        url: `/mqttapi/v1/session/${sessionID}/file/${fileName}/tree/node`,
        headers: {
          'Content-Type': 'application/json',
        },
        data: JSON.stringify(patches),
      }

      axiosPrivate(config)
        .then(function (response) {
          setTree(response.data)
          closeModal()
        })
        .catch(function (error: any) {
          setFetchError(error.response?.data)
        })
    },
    [sessionID, fileName, chosenAction, setFetchError, setTree, type, closeModal]
  )

  return (
    <DialogWrapper isOpen={isOpen} closeModal={closeModal} maxWidth={type === '' || configInfo === null ? 'xs' : 'xs'}>
      {isOpen ? (
          <div style={{ padding: '20px' }}>
            {chosenAction === null ? (
              showActionOptions()
            ) : type === '' && isOpen ? (
              showKindOptions()
            ) : configInfo === null ? (
              <></>
            ) : (
              <>
                <div
                  className={styles.configHeader}
                  style={{
                    borderBottom: `solid 2px ${KindColor({ kind })}`,
                  }}
                >
                  <h4>
                    New {type} {kind}
                  </h4>
                </div>
                <Form
                  schema={configInfo.schema}
                  uiSchema={configInfo.uiSchema}
                  disabled={false}
                  formData={{}}
                  onChange={() => {}}
                  onSubmit={submitConfigChanges}
                  onCancel={closeModal}
                />
              </>
            )}
            {isLoading && <Loading />}
            {fetchError !== null && <ErrorDisplay err={fetchError} />}
          </div>
        )
       : null}
    </DialogWrapper>
  )
}
